import React, { useState } from 'react';
import AuthContext from '../../contexts/AuthContext';
import BibTeXEditor from '../../BibTeXEditor';
import EditReferenceForm from './EditReferenceForm';
import ReferencePreview from './ReferencePreview';

/**
 * ReferenceCard component - Displays a single reference with expand/collapse,
 * edit functionality, and BibTeX display
 */
function ReferenceCard({
  reference,
  editingRef,
  setEditingRef,
  updateReference,
  deleteReference,
  copyBibtex,
  categories,
  hideCategory,
  isSelected,
  onToggleSelect,
  dragHandleProps
}) {
  const [viewMode, setViewMode] = useState('closed'); // 'closed' | 'preview' | 'bibtex'
  const [editingBibtex, setEditingBibtex] = useState(false);
  const [bibtexValue, setBibtexValue] = useState(reference.bibtex_raw);
  const [bibtexError, setBibtexError] = useState('');
  const getFormData = React.useRef(null);
  const { darkMode } = React.useContext(AuthContext);

  const handleCardClick = (e) => {
    // Don't toggle if clicking on buttons, links, checkbox, or the edit form
    if (
      e.target.tagName === 'BUTTON' ||
      e.target.tagName === 'A' ||
      e.target.tagName === 'INPUT' ||
      e.target.tagName === 'TEXTAREA' ||
      e.target.closest('button') ||
      e.target.closest('a') ||
      e.target.closest('.ref-checkbox') ||
      e.target.closest('.edit-form') ||
      e.target.closest('.bibtex-edit-form') ||
      e.target.closest('.preview-container')
    ) {
      return;
    }
    // Toggle between closed and preview mode
    setViewMode(viewMode === 'closed' ? 'preview' : 'closed');
  };

  const handleToggleView = () => {
    // Toggle between preview and bibtex modes
    setViewMode(viewMode === 'preview' ? 'bibtex' : 'preview');
  };

  const handleEditBibtex = () => {
    setEditingBibtex(true);
    setBibtexValue(reference.bibtex_raw);
    setBibtexError('');
  };

  const handleCancelBibtex = () => {
    setEditingBibtex(false);
    setBibtexValue(reference.bibtex_raw);
    setBibtexError('');
  };

  const handleSaveBibtex = async () => {
    try {
      setBibtexError('');
      await updateReference(reference.id, { bibtex_raw: bibtexValue }, true);
      setEditingBibtex(false);
    } catch (err) {
      setBibtexError(err.response?.data?.error || 'Failed to update BibTeX');
    }
  };

  return (
    <div className={`reference-card ${isSelected ? 'selected' : ''}`} onClick={handleCardClick} style={{ cursor: 'pointer' }}>
      <div className="ref-header">
        <div className="ref-header-left">
          {dragHandleProps && (
            <span className="drag-handle" {...dragHandleProps} onClick={(e) => e.stopPropagation()}>
              ⋮⋮
            </span>
          )}
          <label className="ref-checkbox" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={isSelected || false}
              onChange={() => onToggleSelect(reference.id)}
            />
          </label>
          <h3>{reference.title}</h3>
        </div>
        <div className="ref-actions">
          <button onClick={() => {
            if (editingRef === reference.id) {
              // Save data before closing
              if (getFormData.current) {
                const data = getFormData.current();
                updateReference(reference.id, data);
              }
              setEditingRef(null);
            } else {
              setEditingRef(reference.id);
            }
          }}>
            {editingRef === reference.id ? 'Close and Save Notes' : 'Edit Notes'}
          </button>
          <button onClick={() => deleteReference(reference.id)} className="delete-btn">
            Delete
          </button>
        </div>
      </div>

      <p className="ref-meta">
        <strong>Authors:</strong> {reference.author || 'N/A'}
        {'   '}
        <strong>Year:</strong> {reference.year || 'N/A'}
        {'   '}
        <strong>Type:</strong> {reference.entry_type}
      </p>

      <p className="ref-inline">
        {reference.journal && (
          <>
            <strong>Journal:</strong> {reference.journal}
          </>
        )}
        {reference.doi && (
          <span style={{ marginLeft: reference.journal ? '30px' : '0' }}>
            <strong>DOI:</strong>{' '}
            <a
              href={`https://doi.org/${reference.doi.replace('https://doi.org/', '').replace('http://doi.org/', '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="doi-link"
            >
              {reference.doi.replace('https://doi.org/', '').replace('http://doi.org/', '')}
            </a>
          </span>
        )}
      </p>

      {editingRef === reference.id ? (
        <EditReferenceForm
          reference={reference}
          getData={getFormData}
          categories={categories}
        />
      ) : (
        <>
          {!hideCategory && reference.category && <p><strong>Category:</strong> <span className="category-badge">{reference.category}</span></p>}
          {reference.rating && <p><strong>Rating:</strong> {'⭐'.repeat(reference.rating)}</p>}
        </>
      )}

      {viewMode === 'preview' && (
        <ReferencePreview
          reference={reference}
          onToggleToBibtex={handleToggleView}
        />
      )}

      {viewMode === 'bibtex' && (
        <div className="bibtex-container">
          {!editingBibtex ? (
            <>
              <div className="bibtex-actions">
                <button
                  className="preview-toggle-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleView();
                  }}
                  title="Show Preview"
                >
                  ← Show Preview
                </button>
                <button
                  className="copy-bibtex-btn"
                  onClick={() => copyBibtex(reference.bibtex_raw)}
                  title="Copy to clipboard"
                >
                  ⎘ Copy
                </button>
                <button
                  className="edit-bibtex-btn"
                  onClick={handleEditBibtex}
                  title="Edit BibTeX"
                >
                  ✎ Edit
                </button>
              </div>
              <pre className="bibtex-code">{reference.bibtex_raw}</pre>
            </>
          ) : (
            <div className="bibtex-edit-form">
              <div className="bibtex-edit-actions">
                <button onClick={handleSaveBibtex} className="save-bibtex-btn">
                  ✓ Save
                </button>
                <button onClick={handleCancelBibtex} className="cancel-bibtex-btn">
                  ✕ Cancel
                </button>
              </div>
              {bibtexError && <div className="error">{bibtexError}</div>}
              <BibTeXEditor
                className="bibtex-edit-editor"
                value={bibtexValue}
                onChange={(e) => setBibtexValue(e.target.value)}
                height="200px"
                darkMode={darkMode}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ReferenceCard;
